<template>
  <div
    ref="parent"
    class="form-multiselect block w-full bg-transparent border-none text-blue-900 overflow-y-hidden"
  >
    <div ref="title" class="mb-1 inline-flex justify-between w-full">
      <div v-if="title" class="text-left font-semibold">
        {{ title }}
      </div>
      <div class="flex justify-end">
        <button
          v-if="hasSelections && !preventNoSelections"
          class="btn-secondary h-6"
          @click="clearSelections"
        >
          <ClearIcon />
        </button>
        <button
          v-if="collapsable"
          class="bg-default text-white p-1 focus:outline-none"
          @click="collapsed = !collapsed"
        >
          <PlusMinusIcon :sign="!collapsed ? 'minus' : 'plus'" />
        </button>
      </div>
    </div>
    <div v-if="!collapsed">
      <div
        v-if="showSearch"
        ref="search"
        class="inline-flex justify-between w-full mb-4 bg-white appearance-none border border-blue-900  py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
      >
        <div class="inline-flex flex-grow">
          <SearchIcon />
          <input
            id="search-filter"
            v-model="searchText"
            class="pl-2 text-blue-900 w-full"
            type="text"
            placeholder="Search"
            @keyup.esc="handleClearText"
          />
        </div>
        <ClearIcon
          v-if="searchText.length > 0"
          class="flex-initial"
          @click="handleClearText"
        />
      </div>
      <div
        ref="scrollwindow"
        class="overflow-y-auto overflow-x-visible"
        :class="horizontal ? 'w-full flex justify-between' : ''"
        style="scrollbar-color: #090;"
        :style="{ 'max-height': collapsable ? '16rem' : 'none' }"
      >
        <div v-for="(value, index) in values" :key="index">
          <QlikFilterItem
            v-if="
              (value.isSelected ||
                (value.text &&
                  value.text
                    .toLowerCase()
                    .includes(searchText.toLowerCase()))) &&
                (!value.isExcluded || !hideExcluded)
            "
            :text="value.text"
            :number="value.number"
            :is-selected="value.isSelected"
            :is-excluded="!preventExclusionStyle && value.isExcluded"
            :text-size="textSize"
            @click="handleItemClick"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QlikFilterItem from './QlikFilterItem'
import SearchIcon from '~sdp-components/Icons/SearchIcon'
import ClearIcon from '~sdp-components/Icons/ClearIcon'
import PlusMinusIcon from '~sdp-components/Icons/PlusMinusIcon'

export default {
  components: {
    QlikFilterItem,
    SearchIcon,
    ClearIcon,
    PlusMinusIcon
  },
  props: {
    // required
    field: {
      type: String,
      required: true
    },

    // optional
    filteringMeasure: {
      type: String,
      required: false,
      default: null
    },

    preventSortByState: {
      type: Boolean,
      required: false,
      default: false
    },
    preventMultipleSelections: {
      type: Boolean,
      required: false,
      default: false
    },
    preventNoSelections: {
      type: Boolean,
      required: false,
      default: false
    },
    preventSearch: {
      type: Boolean,
      required: false,
      defualt: false
    },
    hideExcluded: {
      type: Boolean,
      required: false,
      default: false
    },
    /** Set to true to not gray out exclusions */
    preventExclusionStyle: {
      type: Boolean,
      required: false,
      default: false
    },
    title: {
      type: String,
      required: false,
      default: ''
    },
    maxValues: {
      type: Number,
      required: false,
      default: 500
    },
    collapsable: {
      type: Boolean,
      required: false,
      default: false
    },
    horizontal: {
      type: Boolean,
      required: false,
      default: false
    },
    textSize: {
      type: String,
      required: false,
      default: 'small'
    }
  },
  data() {
    return {
      sessionObject: null,
      searchText: '',
      longListSize: 6, // determines whether we have a search and float selected to the top
      values: [],
      collapsed: false
    }
  },
  computed: {
    hasSelections() {
      return this.values.filter(val => val.isSelected).length > 0
    },
    showSearch() {
      return !this.preventSearch && this.values.length > this.longListSize
    }
  },
  watch: {
    field() {
      this.initialize()
    }
  },

  /**
   * Mounted will initialize the values of the filter. Once updated the height of the scrollwindow will be set
   */
  async mounted() {
    await this.initialize()
    // set the scrollwindow's height to that of the parent - title stuff, this will force the scroll just to the scroll area
    if (this.$refs.scrollwindow) {
      const scrollHeight =
        this.$refs.parent.clientHeight -
        ((this.$refs.title ? this.$refs.title.clientHeight + 15 : 0) +
          (this.$refs.search ? this.$refs.search.clientHeight + 15 : 0))
      this.$refs.scrollwindow.style.height = scrollHeight + 'px'
      // console.log("mounted", scrollHeight, this.$refs)
    }
  },

  destroyed() {
    this.removeQlikListeners()
  },

  methods: {
    async initialize() {
      this.collapsed = this.collapsable
      try {
        // has this already been set up, if so remove old listener
        if (this.sessionObject) {
          await this.removeQlikListeners()
          this.values = []
        }
        if (this.filteringMeasure) {
          this.sessionObject = await this.$qlik.generateFieldMeasureHypercubeObjectFromDef(
            this.field,
            {
              state: !this.preventSortByState ? 1 : 0,
              numeric: 1,
              ascii: 1
            },
            this.filteringMeasure
          )
        } else {
          this.sessionObject = await this.$qlik.generateListObject(this.field, {
            state: !this.preventSortByState ? 1 : 0,
            numeric: 1,
            ascii: 1
          })
        }
        this.sessionObject.on('changed', this.update)
        this.values = await this.getValues()
        this.initialOrder = this.values.map(v => v.text)
      } catch (err) {
        console.log('error in QlikFilter', err)
      }
    },

    async getValues() {
      let values
      if (this.filteringMeasure) {
        values = await this.$qlik.getValuesFromHypercubeObject(
          this.sessionObject
        )
        values = values[this.field]
        // for some unknown reason, sorting by state may not be working, do it manually
        values = values.sort((a, b) => {
          if (a.state === b.state) {
            return 0
          } else if (a.state === 'S') {
            return -1
          } else if (b.state === 'S') {
            return 1
          } else if (a.state === 'A') {
            return -1
          } else if (b.state === 'A') {
            return 1
          } else {
            return 0
          }
        })
      } else {
        values = await this.$qlik.getValuesFromListObject(this.sessionObject)
      }
      return values
    },

    areValuesEqual(values1, values2) {
      if (values1.length === values2.length) {
        return JSON.stringify(values1) === JSON.stringify(values2)
      } else {
        return false
      }
    },

    /**
     * A qlik filter, after a field is select updates current selections in the store.
     */
    async update() {
      const previousValues = this.values
      this.values = await this.getValues()
      if (!this.areValuesEqual(previousValues, this.values)) {
        const selections = this.values.filter(value => value.isSelected)
        this.$emit('change', { field: this.field, selections })
      }
    },

    clearSelections() {
      this.$qlik.clearField(this.field)
    },

    /**
     * When a user selects an item the effect is determined by the preventMultipleSelections and preventNoSelections props.
     * If clicking on a possible (not selected) value and preventMultipleSelections is false then add this to set of current selections,
     * if preventMultipleSelections is true replace with this value
     * If clicking on a currently selected value and preventNoSelections is false then remove this item from the list, else do nothing
     * If false, then remove this item from the list of selected items.
     * This function does not impact the view, rather, a change listener waits for Qlik engine to report changes.
     */
    async handleItemClick({ text, number }) {
      try {
        // this is a toggle, so remove a clicked value if it is already selected
        // but don't affect the state, let update do that, checking the actual qlik field selections
        let selectedValues = this.values.filter(val =>
          val.isSelected ? val.isSelected : false
        )
        const index = selectedValues.map(val => val.text).indexOf(text)
        if (index >= 0) {
          if (selectedValues.length > 1 || !this.preventNoSelections) {
            selectedValues.splice(index, 1)
          }
        } else if (!this.preventMultipleSelections) {
          selectedValues.push({ text, number })
        } else {
          selectedValues = [{ text, number }]
        }

        await this.$qlik.selectFieldValues(this.field, selectedValues)

        // if there are more than five items
        // and and we are not at the top of the page when a user selects something
        // animate a fast scroll to the top
        if (
          !this.preventSortByState &&
          this.$refs.scrollwindow &&
          this.$refs.scrollwindow.scrollTop
        ) {
          const scrollChange = this.$refs.scrollwindow.scrollTop / 4
          const animateScroll = () => {
            const scrollTop = this.$refs.scrollwindow.scrollTop
            if (scrollTop < scrollChange) {
              this.$refs.scrollwindow.scrollTop = 0
            } else {
              this.$refs.scrollwindow.scrollTop = scrollTop - scrollChange
              setTimeout(animateScroll, 66)
            }
          }
          setTimeout(animateScroll, 66)
        }
      } catch (error) {
        console.log('error in QlikFilter: handleSelection', error)
      }
    },

    handleClearText() {
      console.log('handleClearText')
      this.searchText = ''
    },

    async removeQlikListeners() {
      if (this.$store.state.qdtComponents && this.sessionObject) {
        await this.sessionObject.removeListener('changed', this.update)
        return await this.$qlik.destroySessionObject(this.sessionObject)
      } else {
        return false
      }
    }
  }
}
</script>
<style scoped>
.bg-default {
  background-color: #2a4365;
}
.bg-selections {
  background-color: #009845;
}
/* width */
::-webkit-scrollbar {
  width: 10px;
}
/* Track */
::-webkit-scrollbar-track {
  @apply bg-blue-200;
}
/* Handle */
::-webkit-scrollbar-thumb {
  @apply bg-blue-300;
}
/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  @apply bg-blue-500;
}
</style>
